import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import {
  BdPermissionsTableComponent,
  PermissionEntry,
} from '../../permissions/permissions-table.component';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { take } from 'rxjs';

import {
  BdPermissionDetailDialogComponent,
  PermissionDetailDialogData,
} from './permission-detail-dialog.component';
import { BdPendingRequestsTableComponent } from './pending-requests-table.component';
import { DIALOG_CONFIG_FOR_FILTERS } from '@bindoc-projects/util-lib/ui';

export interface PermissionsWithDetailsDialogData {
  shareableType: ShareableType;
  shareableId: string;
  shareableName?: string;
}

@Component({
  selector: 'bd-permissions-with-details-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-4 p-4">
      <bd-permissions-table
        [shareableType]="data.shareableType"
        [shareableId]="data.shareableId"
        [shareableName]="data.shareableName ?? ''"
        (detailRequested)="onDetail($event)"
      ></bd-permissions-table>
      <bd-pending-requests-table
        [shareableType]="data.shareableType"
        [shareableId]="data.shareableId"
        [shareableName]="data.shareableName ?? ''"
      ></bd-pending-requests-table>
    </div>
  `,
  imports: [BdPermissionsTableComponent, BdPendingRequestsTableComponent],
})
export class BdPermissionsWithDetailsDialogComponent implements OnInit {
  private readonly _config =
    inject<DynamicDialogConfig<PermissionsWithDetailsDialogData>>(
      DynamicDialogConfig,
    );
  private readonly _dialogService = inject(DialogService);
  private readonly _authService = inject(BdAuthenticationService);

  public readonly data: PermissionsWithDetailsDialogData = this._requireData();
  public readonly currentUserId = signal<string | null>(null);

  ngOnInit(): void {
    this._authService.accountInfo$
      .pipe(take(1))
      .subscribe((account) =>
        this.currentUserId.set(account?.localAccountId ?? null),
      );
  }

  public onDetail(payload: {
    entry: PermissionEntry;
    resolvedName: string;
  }): void {
    const config: DynamicDialogConfig<PermissionDetailDialogData> = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Berechtigungen',
      data: {
        shareableType: this.data.shareableType,
        shareableId: this.data.shareableId,
        shareableName: this.data.shareableName,
        shareTarget: payload.entry.shareTarget,
        shareTargetName: payload.resolvedName,
        allowedPermissions: payload.entry.allowedShareablePermissions,
        isCurrentUser:
          payload.entry.shareTarget.type === 'user' &&
          payload.entry.shareTarget.id === this.currentUserId(),
      },
    };

    this._dialogService.open(BdPermissionDetailDialogComponent, config);
  }

  private _requireData(): PermissionsWithDetailsDialogData {
    if (!this._config.data) {
      throw new Error(
        'BdPermissionsWithDetailsDialogComponent requires dialog data',
      );
    }
    return this._config.data;
  }
}
